import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import {
  Icon,
  Input,
  Link,
  Pagination,
  Table,
  Text,
  Thumbnail,
  Empty,
} from 'components';
import styled from 'styled-components';
import {
  CHAT_SESSION_STORAGE_KEYS,
  buildListingFullAddress,
  featureEnabled,
  formatCurrency,
  paramaterize,
} from 'utils';
import { useDebounce } from 'hooks';
import { ListingController } from 'api';
import { SupraCoopType } from '@bb/domain/dist/types/SupraSystemSubscription';
import { AnsweringSearchListingsDTO } from '@bb/domain/dist/types';
import { CrossOrgAnsweringContext, ListingLabelContext } from 'contexts';
import { ColumnsType } from 'antd/es/table';
import { Section, StyledCard } from '../styles';
import MLSUserPageContext from '../MLSUserPageContext';

type ListingQueryType = {
  queryString: string;
  page: number;
};

type ListingSectionProps = {};

const LISTING_PAGE_SIZE = 20;

const ListingSection: React.FC<ListingSectionProps> = () => {
  const { answeringConfirm } = useContext(CrossOrgAnsweringContext) || {};
  const { user, userId, loading } = useContext(MLSUserPageContext);
  const { getAvailability } = useContext(ListingLabelContext);
  const [fetchingListing, setFetchingListing] = useState(false);
  const [listings, setListings] = useState<AnsweringSearchListingsDTO[]>([]);
  const [totalListingsCount, setTotalListingsCount] = useState(0);
  const [listingQuery, setListingQuery] = useState<ListingQueryType>({
    queryString: '',
    page: 0,
  });
  const debouncedListingQuery = useDebounce(listingQuery.queryString, 500);

  useEffect(() => {
    const fetchListings = async () => {
      let fetchedListings;
      let listingsCount;

      if (
        (featureEnabled('supraAutoCoopFeature') &&
          user.coopType === SupraCoopType.AUTO_COOP) ||
        (featureEnabled('supraManualCoopFeature') &&
          user.coopType === SupraCoopType.MANUAL_COOP)
      ) {
        ({ listings: fetchedListings, count: listingsCount } =
          await ListingController.getListingsByUser({
            userId,
            search: debouncedListingQuery,
            page: listingQuery.page,
            pageSize: LISTING_PAGE_SIZE,
            includeAllAvail: true,
          }));
      } else {
        ({ listings: fetchedListings, count: listingsCount } =
          await ListingController.findAnsweringListings({
            search: debouncedListingQuery,
            page: listingQuery.page,
            repId: userId,
            pageSize: LISTING_PAGE_SIZE,
          }));
      }

      setListings(fetchedListings);
      setTotalListingsCount(listingsCount);

      setFetchingListing(false);
    };
    if (user && userId) {
      setFetchingListing(true);
      setListings([]);
      fetchListings();
    }
  }, [userId, debouncedListingQuery, listingQuery.page, user]);

  const columns: ColumnsType<AnsweringSearchListingsDTO> = [
    {
      key: 'address',
      dataIndex: 'thumbnail',
      title: 'LISTING',
      width: 325,
      render: (
        thumbnail,
        { line1, line2 = '', city, province, postal_code },
      ) => (
        <StyledContainer>
          <StyledThumbnailContainer>
            <Thumbnail src={thumbnail} />
          </StyledThumbnailContainer>
          {buildListingFullAddress(line1, line2)}
          <br />
          {`${city}, ${province}, ${postal_code}`}
        </StyledContainer>
      ),
    },
    {
      key: 'price',
      dataIndex: 'list_price',
      title: 'PRICE',
      render: price => formatCurrency(price),
    },
    {
      title: 'MLS NUMBER',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      render: (status, { mls_numbers, coming_soon_date }) => (
        <>
          {mls_numbers ? (
            mls_numbers.map(({ board, number }, i) => (
              <Text ellipsis key={i} block>
                {board}: {number}
              </Text>
            ))
          ) : (
            <div>{status}</div>
          )}
          {status === 'Coming Soon' && coming_soon_date && (
            <div>{moment(coming_soon_date).format('MMM D, YYYY')}</div>
          )}
        </>
      ),
    },
    {
      key: 'availability',
      dataIndex: 'availability',
      title: 'AVAIL/STATUS',
      render: availability => getAvailability(availability),
    },
  ];

  if (!userId) return null;

  return (
    <Section data-cy={'listing-section'}>
      <StyledCard
        title="Listings"
        extra={
          <StyledContainer>
            <Text type="secondary">
              {listings?.length
                ? `${LISTING_PAGE_SIZE * listingQuery.page} - ${
                    LISTING_PAGE_SIZE * listingQuery.page + listings.length
                  } of ${totalListingsCount} listings`
                : ''}
            </Text>
            <Input
              prefix={<Icon type="search" color="gray9" offset={[0, -1]} />}
              onChange={e =>
                setListingQuery({
                  ...listingQuery,
                  queryString: e.target.value,
                })
              }
              type="text"
              placeholder="Search listings"
            />
            {!featureEnabled('supraAutoCoopFeature') ||
              (user?.coopType === SupraCoopType.PRIMARY && (
                <Link
                  onClick={answeringConfirm({ type: 'agent' }, () => {
                    CHAT_SESSION_STORAGE_KEYS.forEach(key =>
                      sessionStorage.removeItem(key),
                    );
                    window.location.href = `/answering/assume_user/${userId}?${paramaterize(
                      {
                        redirectUrl: `/listing/list/brokerage/0?${paramaterize({
                          agent: userId,
                        })}`,
                      },
                    )}`;
                  })}
                >
                  View all
                </Link>
              ))}
          </StyledContainer>
        }
      >
        <Table
          columns={columns}
          dataSource={listings}
          pagination={false}
          scroll={{ y: 300 }}
          loading={loading || fetchingListing}
          locale={{
            emptyText: (
              <Empty description={'There are currently no listings'} />
            ),
          }}
          rowKey="id"
        />
        <StyledPagination
          current={listingQuery.page + 1}
          pageSize={LISTING_PAGE_SIZE}
          total={totalListingsCount}
          onChange={page =>
            setListingQuery({
              ...listingQuery,
              page: page - 1,
            })
          }
          showSizeChanger={false}
        />
      </StyledCard>
    </Section>
  );
};

export default ListingSection;

const StyledPagination = styled(Pagination)`
  margin-top: 5px;
  margin-bottom: 5px;
  text-align: center;
`;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const StyledThumbnailContainer = styled.div`
  width: 50px;
`;
